/**
 * Supabase Client Configuration
 * Production-ready setup for real-time database access
 */

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const IS_PROD = import.meta.env.PROD;
const APP_URL = import.meta.env.VITE_APP_URL?.replace(/\/$/, '');
const FALLBACK_PRODUCTION_APP_URL = 'https://caterer.mrscater.com';

// Validate required environment variables
const requiredEnvVars = {
  VITE_SUPABASE_URL: SUPABASE_URL,
  VITE_SUPABASE_ANON_KEY: SUPABASE_ANON_KEY,
};

const missingVars = Object.entries(requiredEnvVars)
  .filter(([key, value]) => !value)
  .map(([key]) => key);

if (missingVars.length > 0) {
  const errorMessage = `Missing required environment variables: ${missingVars.join(', ')}. Please check your .env file.`;
  console.error(errorMessage);
  
  // In development, show helpful error
  if (import.meta.env.DEV) {
    throw new Error(errorMessage);
  }
  
  // In production, still create client but log error
  console.warn('Supabase client created with missing environment variables. Some features may not work.');
}

export const supabase = createClient(SUPABASE_URL || '', SUPABASE_ANON_KEY || '');

export const getAuthRedirectUrl = (path = '/reset-password') => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (APP_URL) return `${APP_URL}${normalizedPath}`;
  if (typeof window === 'undefined') return normalizedPath;

  const { origin, hostname } = window.location;
  if (IS_PROD && /^(localhost|127\.0\.0\.1)$/i.test(hostname)) {
    return `${FALLBACK_PRODUCTION_APP_URL}${normalizedPath}`;
  }

  return `${origin}${normalizedPath}`;
};

// ============================================================================
// AI TOOLS (GROQ via Supabase Edge Function)
// ============================================================================

const AI_TOOLS_ENDPOINT =
  (SUPABASE_URL ? SUPABASE_URL.replace(/\/$/, '') : '') + '/functions/v1/ai-caterer-tools';

export const aiToolsOperations = {
  /**
   * tool: 'recipe_pricing' | 'invoice_summary' | 'client_message'
   * payload: arbitrary JSON sent to the Edge Function (caterer_id, recipe, order, client, etc.).
   */
  runTool: async (tool, payload) => {
    if (!SUPABASE_URL) {
      throw new Error('VITE_SUPABASE_URL is not configured');
    }
    const res = await fetch(AI_TOOLS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY || '',
        // Some Supabase setups require an Authorization bearer even for Edge Functions.
        Authorization: `Bearer ${SUPABASE_ANON_KEY || ''}`,
      },
      body: JSON.stringify({ tool, payload }),
    });
    if (!res.ok) {
      let detail = '';
      try {
        const errJson = await res.json();
        detail = errJson?.error || errJson?.detail || '';
      } catch {
        // ignore
      }
      throw new Error(detail || `AI tools request failed (${res.status})`);
    }
    const json = await res.json();
    return json?.result || '';
  },
};

/**
 * Auth operations
 */
export const authOperations = {
  signUp: (email, password) => supabase.auth.signUp({ email, password }),
  signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
  signOut: () => supabase.auth.signOut(),
  getCurrentUser: () => supabase.auth.getUser(),
  onAuthStateChange: (callback) => supabase.auth.onAuthStateChange(callback),
};

/**
 * Referral operations (shared tables with customer app)
 */
export const referralOperations = {
  getByCode: async (code) => {
    const { data, error } = await supabase
      .from('referral_codes')
      .select('id, user_id, role')
      .eq('code', String(code).toUpperCase().trim())
      .maybeSingle();
    return { data, error };
  },
  createRedemption: async (referralCodeId, referredUserId, rewardAmountCents = 5000) => {
    return supabase
      .from('referral_redemptions')
      .insert({ referral_code_id: referralCodeId, referred_user_id: referredUserId, reward_amount_cents: rewardAmountCents })
      .select('id')
      .single();
  },
};

/**
 * Caterer operations
 */
export const catererOperations = {
  // Use limit(1) to avoid multiple rows, get first element from array
  getCaterer: async (userId) => {
    const result = await supabase.from('caterers').select('*').eq('user_id', userId).limit(1);
    if (result.error) return result;
    return { data: result.data?.[0] || null, error: null };
  },
  getCatererById: async (catererId) => {
    const result = await supabase.from('caterers').select('*').eq('id', catererId).limit(1);
    if (result.error) return result;
    return { data: result.data?.[0] || null, error: null };
  },
  updateCaterer: (userId, data) => supabase.from('caterers').update(data).eq('user_id', userId),
  getCatererStats: async (catererId) => {
    const result = await supabase.from('caterer_stats').select('*').eq('caterer_id', catererId).limit(1);
    if (result.error) return result;
    return { data: result.data?.[0] || null, error: null };
  },
};

/**
 * Bid operations
 */
export const bidOperations = {
  // Get all bids received by caterer
  getReceivedBids: (catererId, limit = 20, status = null) => {
    let query = supabase
      .from('bids')
      .select('*, events(*)')
      .eq('caterer_id', catererId);
    
    if (status) {
      query = query.eq('status', status);
    }
    
    return query.order('created_at', { ascending: false }).limit(limit);
  },
  
  // Get all bids submitted by caterer (bids this caterer has made)
  getSubmittedBids: (catererId, limit = 20) =>
    supabase
      .from('bids')
      .select('*, events(*)')
      .eq('caterer_id', catererId)
      .order('created_at', { ascending: false })
      .limit(limit),
  
  getAcceptedBids: (catererId, limit = 10) =>
    supabase
      .from('bids')
      .select('*, events(*)')
      .eq('caterer_id', catererId)
      .eq('status', 'accepted')
      .order('created_at', { ascending: false })
      .limit(limit),
  
  getBidById: (bidId) => supabase.from('bids').select('*, events(*)').eq('id', bidId).single(),
  
  // Accept a bid - move to order
  acceptBid: (bidId, selectedBidId = null) =>
    supabase.from('bids').update({ 
      status: 'accepted', 
      accepted_bid_id: selectedBidId,
      accepted_at: new Date().toISOString(),
      updated_at: new Date() 
    }).eq('id', bidId),
  
  // Decline a bid
  declineBid: (bidId, reason = null) =>
    supabase.from('bids').update({ 
      status: 'declined', 
      declined_at: new Date().toISOString(),
      decline_reason: reason,
      updated_at: new Date() 
    }).eq('id', bidId),
  
  // Submit a counter-offer
  submitCounterOffer: (bidId, counterPrice, counterDetails = null) =>
    supabase.from('bids').update({ 
      status: 'counter_offered', 
      counter_price: counterPrice,
      counter_details: counterDetails,
      counter_offered_at: new Date().toISOString(),
      updated_at: new Date() 
    }).eq('id', bidId),
  
  // Accept counter-offer
  acceptCounterOffer: (bidId) =>
    supabase.from('bids').update({ 
      status: 'accepted', 
      accepted_at: new Date().toISOString(),
      updated_at: new Date() 
    }).eq('id', bidId),
  
  // Reject counter-offer
  rejectCounterOffer: (bidId) =>
    supabase.from('bids').update({ 
      status: 'declined', 
      declined_at: new Date().toISOString(),
      updated_at: new Date() 
    }).eq('id', bidId),
  
  // Mark bid as expired
  expireBid: (bidId) =>
    supabase.from('bids').update({ 
      status: 'expired', 
      expired_at: new Date().toISOString(),
      updated_at: new Date() 
    }).eq('id', bidId),
  
  // Get pending bids awaiting response (bids sent to this caterer)
  getPendingBids: (catererId) =>
    supabase
      .from('bids')
      .select('*, events(*)')
      .eq('caterer_id', catererId)
      .in('status', ['pending', 'counter_offered'])
      .order('created_at', { ascending: false }),

  /**
   * Get open special-request events for verified caterers only (customer requests for bids).
   * Returns events as bid-like objects for IncomingBidsTab. Unverified caterers get [].
   */
  getOpenEvents: async (catererId) => {
    const { data: caterer, error: catererErr } = await supabase
      .from('caterers')
      .select('verification_tier')
      .eq('id', catererId)
      .single();
    if (catererErr || !caterer) return { data: [], error: catererErr || null };
    const tier = caterer.verification_tier;
    if (!tier || tier === 'none') return { data: [], error: null };

    // Only show special requests that have been paid (not sent to caterers before payment)
    const { data: events, error: eventsErr } = await supabase
      .from('events')
      .select('*')
      .eq('status', 'open')
      .eq('is_special_request', true)
      .eq('request_fee_paid', true)
      .order('created_at', { ascending: false });
    if (eventsErr) return { data: [], error: eventsErr };
    if (!events?.length) return { data: [], error: null };

    // Batch-fetch customer emails from users
    const customerIds = [...new Set(events.map((e) => e.customer_id).filter(Boolean))];
    const emailMap = new Map();
    if (customerIds.length > 0) {
      const { data: users } = await supabase.from('users').select('id, email').in('id', customerIds);
      (users || []).forEach((u) => emailMap.set(u.id, u.email));
    }

    const { data: myBids } = await supabase
      .from('bids')
      .select('event_id')
      .eq('caterer_id', catererId);
    const bidEventIds = new Set((myBids || []).map((b) => b.event_id));

    const list = events
      .filter((e) => !bidEventIds.has(e.id))
      .map((e) => {
        const budgetStr = e.budget_max
          ? `R ${Number(e.budget_min || 0).toLocaleString()}-R ${Number(e.budget_max).toLocaleString()}`
          : e.budget_min
            ? `R ${Number(e.budget_min).toLocaleString()}+`
            : 'TBD';
        let cuisinePreferences = [];
        let attachments = e.attachments ?? [];
        const SERVICE_ADDON_LABELS = {
          cutlery: 'Cutlery & Tableware',
          serving: 'Serving Staff',
          cleaning: 'Cleaning Team',
          packaging: 'Original Packaging',
          setup: 'Setup & Breakdown',
          bartender: 'Bartender Service',
          decor: 'Decor Styling',
          sound_mc: 'Sound & MC',
          photography: 'Photography',
        };
        if (e.special_requirements) {
          try {
            const parsed = typeof e.special_requirements === 'string' ? JSON.parse(e.special_requirements) : e.special_requirements;
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
              if (Array.isArray(parsed.requirements)) cuisinePreferences = parsed.requirements;
              else if (Array.isArray(parsed.serviceAddons)) {
                cuisinePreferences = parsed.serviceAddons.map((id) => SERVICE_ADDON_LABELS[id] || id).filter(Boolean);
              }
              else if (Array.isArray(parsed)) cuisinePreferences = parsed;
              if (Array.isArray(parsed.attachments)) attachments = parsed.attachments;
              else if (Array.isArray(parsed.uploadedFiles)) attachments = parsed.uploadedFiles;
              if (cuisinePreferences.length === 0) {
                const parts = [];
                if (parsed.menuPreferences && String(parsed.menuPreferences).trim()) parts.push(String(parsed.menuPreferences).slice(0, 80));
                if (parsed.dietaryRestrictions && String(parsed.dietaryRestrictions).trim()) parts.push(String(parsed.dietaryRestrictions).slice(0, 80));
                if (parsed.specialAccommodations && String(parsed.specialAccommodations).trim()) parts.push(String(parsed.specialAccommodations).slice(0, 80));
                if (parts.length) cuisinePreferences = parts;
                else cuisinePreferences = Object.values(parsed).filter((v) => typeof v === 'string' && v.length < 200);
              }
            } else if (Array.isArray(parsed)) {
              cuisinePreferences = parsed;
            } else {
              cuisinePreferences = [String(e.special_requirements).slice(0, 50)];
            }
          } catch {
            cuisinePreferences = [String(e.special_requirements).slice(0, 80)];
          }
        }
        const customerEmail = emailMap.get(e.customer_id);
        return {
          id: e.id,
          deadline: e.bidding_deadline || e.event_date || e.created_at,
          eventType: e.event_type || e.title || 'Event',
          customerName: customerEmail || 'Customer',
          customerEmail: customerEmail || null,
          guestCount: e.estimated_guests || 0,
          location: e.location || '',
          eventDate: e.event_date,
          eventTime: e.event_time || null,
          budget: budgetStr,
          budgetMin: e.budget_min != null ? Number(e.budget_min) : null,
          budgetMax: e.budget_max != null ? Number(e.budget_max) : null,
          cuisinePreferences,
          description: e.description || '',
          attachments,
          responded: false,
        };
      });
    return { data: list, error: null };
  },
  
  // Get expired bids
  getExpiredBids: (catererId, limit = 10) =>
    supabase
      .from('bids')
      .select('*, events(*)')
      .eq('caterer_id', catererId)
      .eq('status', 'expired')
      .order('created_at', { ascending: false })
      .limit(limit),
  
  // Create/submit a new bid
  createBid: async (bidData) => {
    const { data, error } = await supabase.from('bids').insert(bidData).select('id').single();
    if (!error && data?.id) {
      try {
        await supabase.functions.invoke('notify-bid', { body: { bidId: data.id } });
      } catch (e) {
        // Non-blocking – email notifications shouldn't block bid creation
        console.warn('[createBid] Failed to notify customer about bid', e);
      }
    }
    return { data, error };
  },
  
  // Check for expired bids and mark them
  expireOldBids: async (catererId) => {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    
    // First, fetch the bids that should be expired
    const { data: oldBids, error: fetchError } = await supabase
      .from('bids')
      .select('id')
      .eq('caterer_id', catererId)
      .eq('status', 'pending')
      .lt('created_at', thirtyDaysAgo);
    
    if (fetchError) {
      console.error('Error fetching old bids:', fetchError);
      return { data: null, error: fetchError };
    }
    
    if (!oldBids || oldBids.length === 0) {
      return { data: [], error: null, count: 0 };
    }
    
    // Extract IDs
    const bidIds = oldBids.map(bid => bid.id);
    
    // Now update those specific bids
    const result = await supabase
      .from('bids')
      .update({ status: 'expired', expired_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .in('id', bidIds);
    
    return result;
  },
  
  updateBidStatus: (bidId, status) =>
    supabase.from('bids').update({ status, updated_at: new Date() }).eq('id', bidId),
};

/**
 * Order operations
 */
export const orderOperations = {
  /** Active orders: paid only. Caterer must not see orders until customer has paid (like normal checkout). */
  getPendingOrders: (catererId, limit = 10) =>
    supabase
      .from('orders')
      .select(`
        *,
        events (*),
        customer:users (*),
        order_items (
          id,
          menu_item_id,
          name,
          quantity,
          unit_price,
          subtotal,
          category,
          special_instructions
        )
      `)
      .eq('caterer_id', catererId)
      .in('payment_status', ['paid', 'deposit_paid'])
      .neq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(limit),

  /** Fetch ALL orders for caterer (paid/deposit-paid – caterers see orders after payment) */
  getAllOrders: (catererId, limit = 100) =>
    supabase
      .from('orders')
      .select(`
        *,
        events (*),
        customer:users (*),
        order_items (
          id,
          menu_item_id,
          name,
          quantity,
          unit_price,
          subtotal,
          category,
          special_instructions
        )
      `)
      .eq('caterer_id', catererId)
      .in('payment_status', ['paid', 'deposit_paid'])
      .order('created_at', { ascending: false })
      .limit(limit),

  /** Kitchen queue: fetch all upcoming/active orders regardless of payment split model. */
  getKitchenOrders: (catererId, limit = 200) =>
    supabase
      .from('orders')
      .select(`
        *,
        events (*),
        customer:users (*),
        order_items (
          id,
          menu_item_id,
          name,
          quantity,
          unit_price,
          subtotal,
          category,
          special_instructions
        )
      `)
      .eq('caterer_id', catererId)
      .in('status', ['pending', 'confirmed', 'in_progress', 'ready_for_delivery'])
      .order('delivery_date', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true })
      .limit(limit),

  getOrderById: (orderId) =>
    supabase
      .from('orders')
      .select(`
        *,
        events (*),
        customer:users (*)
      `)
      .eq('id', orderId)
      .single(),

  updateOrderStatus: (orderId, status) =>
    supabase
      .from('orders')
      .update({ status, updated_at: new Date() })
      .eq('id', orderId),

  acceptOrder: (orderId, catererNotes = null) =>
    supabase
      .from('orders')
      .update({
        status: 'confirmed',
        accepted_at: new Date().toISOString(),
        caterer_notes: catererNotes,
        updated_at: new Date()
      })
      .eq('id', orderId),

  rejectOrder: (orderId, rejectionReason = null) =>
    supabase
      .from('orders')
      .update({
        status: 'cancelled',
        rejected_at: new Date().toISOString(),
        rejection_reason: rejectionReason,
        updated_at: new Date()
      })
      .eq('id', orderId),

  startPreparation: (orderId) =>
    supabase
      .from('orders')
      .update({
        status: 'in_progress',
        preparation_started_at: new Date().toISOString(),
        updated_at: new Date()
      })
      .eq('id', orderId),

  completePreparation: async (orderId, estimatedDelivery = null) => {
    // Only set estimated_delivery_time if it's a valid ISO timestamp
    // If it's a string like "1 hour", ignore it
    const updateData = {
      status: 'ready_for_delivery',
      preparation_completed_at: new Date().toISOString(),
      updated_at: new Date()
    };
    
    // Only add estimated_delivery_time if it's a valid ISO timestamp string
    if (estimatedDelivery && typeof estimatedDelivery === 'string') {
      // Check if it's a valid ISO timestamp (contains 'T' or is a valid date)
      if (estimatedDelivery.includes('T') || !isNaN(Date.parse(estimatedDelivery))) {
        updateData.estimated_delivery_time = estimatedDelivery;
      }
      // Otherwise, ignore invalid values like "1 hour"
    } else if (estimatedDelivery && estimatedDelivery instanceof Date) {
      updateData.estimated_delivery_time = estimatedDelivery.toISOString();
    }
    
    const { data, error } = await supabase
      .from('orders')
      .update(updateData)
      .eq('id', orderId)
      .select('id, customer_id, total_amount, estimated_delivery_time, delivered_at')
      .single();

    return { data, error };
  },

  markDelivered: async (orderId, deliveryTime = null, deliveryNotes = null) => {
    const { data, error } = await supabase
      .from('orders')
      .update({
        status: 'completed',
        completed_date: new Date().toISOString(),
        delivered_at: deliveryTime || new Date().toISOString(),
        delivery_notes: deliveryNotes,
        updated_at: new Date()
      })
      .eq('id', orderId)
      .select('id, customer_id, total_amount, delivered_at, estimated_delivery_time')
      .single();

    // Fire-and-forget email to customer that order is completed
    if (!error && data?.customer_id) {
      try {
        const { data: customer } = await supabase
          .from('users')
          .select('email')
          .eq('id', data.customer_id)
          .single();

        const customerEmail = customer?.email;
        if (customerEmail) {
          const deliveryTimeStr =
            data.delivered_at ||
            data.estimated_delivery_time ||
            new Date().toISOString();

          await supabase.functions.invoke('send-email', {
            body: {
              to: customerEmail,
              subject: 'Your order has been completed – Mrs Cater',
              template: 'order-status',
              data: {
                status: 'completed',
                orderNumber: data.id,
                eventName: undefined,
                deliveryTime: deliveryTimeStr,
                total: data.total_amount,
              },
            },
          });
        }
      } catch (err) {
        // Non-blocking – log and continue
        console.warn('[Orders] Failed to send completed-order email', err);
      }
    }

    return { data, error };
  },

  /** Confirm delivery by entering the code the customer gave you.
   * Calls confirm-delivery-by-code edge function (NO JWT required - code acts as secret). */
  confirmDeliveryByCode: async (orderId, deliveryCode) => {
    const code = String(deliveryCode).trim();
    if (!code) return { data: null, error: new Error('Enter the delivery code the customer gave you.') };
    const baseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
    if (!baseUrl) return { data: null, error: new Error('Missing Supabase URL.') };
    if (!anonKey) return { data: null, error: new Error('Missing Supabase key.') };
    
    const url = `${baseUrl}/functions/v1/confirm-delivery-by-code`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey
      },
      body: JSON.stringify({ orderId, deliveryCode: code }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = data?.error ?? data?.detail ?? `Failed (${res.status})`;
      return { data: null, error: new Error(message) };
    }
    return { data, error: null };
  },

  getOrdersByStatus: (catererId, status) =>
    supabase
      .from('orders')
      .select(`
        *,
        events (*),
        customer:users (*)
      `)
      .eq('caterer_id', catererId)
      .eq('payment_status', 'paid')
      .eq('status', status)
      .order('created_at', { ascending: false }),

  getDeliveryHistory: (catererId, limit = 20) =>
    supabase
      .from('orders')
      .select(`
        *,
        events (*),
        customer:users (*)
      `)
      .eq('caterer_id', catererId)
      .eq('payment_status', 'paid')
      .eq('status', 'completed')
      .order('delivered_at', { ascending: false })
      .limit(limit),
};


/**
 * Menu operations
 */
export const menuOperations = {
  getCatererMenu: (catererId) =>
    supabase
      .from('menu_items')
      .select('*')
      .eq('caterer_id', catererId)
      .order('created_at', { ascending: false }),
  
  getCatererMenuWithStats: (catererId) =>
    supabase
      .from('menu_items')
      .select(`
        id,
        name,
        price,
        order_count,
        total_revenue,
        avg_rating
      `)
      .eq('caterer_id', catererId)
      .order('order_count', { ascending: false }),
  
  getMenuItemById: (itemId) => supabase.from('menu_items').select('*').eq('id', itemId).single(),
  
  createMenuItem: (catererId, data) =>
    supabase.from('menu_items').insert({ caterer_id: catererId, ...data, created_at: new Date() }),
  
  updateMenuItem: (itemId, data) =>
    supabase.from('menu_items').update({ ...data, updated_at: new Date() }).eq('id', itemId),
  
  deleteMenuItem: (itemId) => supabase.from('menu_items').delete().eq('id', itemId),
};

/**
 * Menu add-on operations (add-ons attached to menu items)
 */
export const menuAddonOperations = {
  getByCaterer: (catererId) =>
    supabase
      .from('menu_addons')
      .select('*')
      .eq('caterer_id', catererId)
      .order('menu_item_id')
      .order('name'),

  getByMenuItemId: (menuItemId) =>
    supabase
      .from('menu_addons')
      .select('*')
      .eq('menu_item_id', menuItemId)
      .order('name'),

  create: (catererId, menuItemId, data) =>
    supabase
      .from('menu_addons')
      .insert({
        caterer_id: catererId,
        menu_item_id: menuItemId,
        name: data.name,
        price: data.price ?? 0,
      })
      .select('*')
      .single(),

  update: (addonId, data) =>
    supabase
      .from('menu_addons')
      .update({ name: data.name, price: data.price ?? 0, updated_at: new Date().toISOString() })
      .eq('id', addonId)
      .select('*')
      .single(),

  delete: (addonId) =>
    supabase.from('menu_addons').delete().eq('id', addonId),
};

/**
 * Availability operations
 */
export const availabilityOperations = {
  getCatererAvailability: (catererId) =>
    supabase
      .from('availability')
      .select('*')
      .eq('caterer_id', catererId)
      .order('date', { ascending: true }),
  
  blockDate: (catererId, data) =>
    supabase.from('availability_blocks').insert({
      caterer_id: catererId,
      ...data,
      created_at: new Date(),
    }),
  
  removeBlock: (blockId) => supabase.from('availability_blocks').delete().eq('id', blockId),
  
  getBlockedDates: (catererId, startDate, endDate) =>
    supabase
      .from('availability_blocks')
      .select('*')
      .eq('caterer_id', catererId)
      .gte('date', startDate)
      .lte('date', endDate),
};

/**
 * Revenue operations
 */
export const revenueOperations = {
  getCatererRevenue: (catererId, startDate, endDate) =>
    supabase
      .from('revenue')
      .select('*')
      .eq('caterer_id', catererId)
      .gte('created_at', startDate)
      .lte('created_at', endDate),
  
  getCatererRevenueByDateRange: (catererId, startDate, endDate) => {
    const start = startDate.toISOString().split('T')[0];
    const end = endDate.toISOString().split('T')[0];
    return supabase
      .from('revenue')
      .select('*')
      .eq('caterer_id', catererId)
      .gte('created_at', start)
      .lte('created_at', end)
      .order('created_at', { ascending: true });
  },
  
  getPayoutHistory: (catererId, limit = 12) =>
    supabase
      .from('payouts')
      .select('*')
      .eq('caterer_id', catererId)
      .order('created_at', { ascending: false })
      .limit(limit),

  getCommissionBreakdown: (catererId, startDate, endDate) =>
    supabase
      .from('commission_transactions')
      .select('order_subtotal, commission_amount, caterer_net_amount, menu_markup_amount')
      .eq('caterer_id', catererId)
      .gte('created_at', startDate)
      .lte('created_at', endDate),

  /** Escrow balance: sum of caterer_net_amount held in escrow (not yet released) */
  getEscrowBalance: (catererId) =>
    supabase
      .from('order_escrow')
      .select('caterer_net_amount')
      .eq('caterer_id', catererId)
      .eq('escrow_status', 'escrowed')
      .eq('released_to_caterer', false),

  /** Full escrow items for display: what is currently in escrow (order details included) */
  getEscrowItems: (catererId) =>
    supabase
      .from('order_escrow')
      // Use explicit FK name to disambiguate relationship between order_escrow and orders:
      // - order_escrow.order_id -> orders.id (order_escrow_order_id_fkey)
      // - orders.escrow_id      -> order_escrow.id (orders_escrow_id_fkey)
      // Without the !fk notation, PostgREST sees two relationships and throws:
      // \"Could not embed because more than one relationship was found for 'order_escrow' and 'orders'\"
      .select(
        'id, order_id, caterer_net_amount, gross_amount, escrow_status, created_at, escrowed_at, ' +
        'orders!order_escrow_order_id_fkey(id, delivery_date, delivery_time, event_id)'
      )
      .eq('caterer_id', catererId)
      .eq('escrow_status', 'escrowed')
      .eq('released_to_caterer', false)
      .order('created_at', { ascending: false }),
};

/**
 * Payout operations
 */
export const payoutOperations = {
  requestPayout: (catererId, data) =>
    supabase
      .from('payouts')
      .insert({
        caterer_id: catererId,
        amount: data?.amount ?? 0,
        payout_method: data?.payout_method ?? 'bank_transfer',
        status: 'pending',
        requested_date: new Date().toISOString(),
        created_at: new Date().toISOString(),
      })
      .select('id')
      .single(),
  
  getPayoutById: (payoutId) =>
    supabase.from('payouts').select('*').eq('id', payoutId).single(),
  
  getPayoutsByStatus: (catererId, status) =>
    supabase
      .from('payouts')
      .select('*')
      .eq('caterer_id', catererId)
      .eq('status', status)
      .order('created_at', { ascending: false }),
  
  updatePayoutStatus: (payoutId, status, data = {}) =>
    supabase
      .from('payouts')
      .update({
        status,
        ...data,
        updated_at: new Date(),
      })
      .eq('id', payoutId),
  
  getAllPayouts: (catererId) =>
    supabase
      .from('payouts')
      .select('*')
      .eq('caterer_id', catererId)
      .order('created_at', { ascending: false }),
};

/**
 * Portfolio operations
 */
export const portfolioOperations = {
  getCatererPortfolio: (catererId) =>
    supabase
      .from('portfolio_photos')
      .select('*')
      .eq('caterer_id', catererId)
      .order('created_at', { ascending: false }),
  
  createPortfolioPhoto: (catererId, data) =>
    supabase.from('portfolio_photos').insert({
      caterer_id: catererId,
      ...data,
      created_at: new Date(),
    }),
  
  updatePortfolioPhoto: (photoId, data) =>
    supabase.from('portfolio_photos').update({ ...data, updated_at: new Date() }).eq('id', photoId),
  
  deletePortfolioPhoto: (photoId) =>
    supabase.from('portfolio_photos').delete().eq('id', photoId),
  
  uploadPortfolioImage: (bucket, filePath, file) =>
    supabase.storage.from(bucket).upload(filePath, file),
  
  deletePortfolioImage: (bucket, filePath) =>
    supabase.storage.from(bucket).remove([filePath]),
  
  getPortfolioImageUrl: async (bucket, filePath) => {
    // For private buckets, use signed URLs with 1 year expiry
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(filePath, 31536000); // 1 year in seconds
    return { data, error };
  },
};

/**
 * Messaging operations (customer <-> caterer)
 * Messages use users table: sender_id/recipient_id are user UUIDs.
 * Caterer uses their user_id (auth.uid()); customers are identified by user id.
 */
export const catererMessagingOperations = {
  ACTIVE_CHAT_ORDER_STATUSES: ['pending', 'confirmed', 'in_progress', 'ready_for_delivery'],
  getMessages: async (catererUserId, direction = 'both') => {
    let query = supabase.from('messages').select('*');
    if (direction === 'sent') {
      query = query.eq('sender_id', catererUserId);
    } else if (direction === 'received') {
      query = query.eq('recipient_id', catererUserId);
    } else {
      query = query.or(`sender_id.eq.${catererUserId},recipient_id.eq.${catererUserId}`);
    }
    return query.order('created_at', { ascending: false }).limit(200);
  },

  getConversation: async (catererUserId, customerUserId) => {
    return supabase
      .from('messages')
      .select('*')
      .or(`and(sender_id.eq.${catererUserId},recipient_id.eq.${customerUserId}),and(sender_id.eq.${customerUserId},recipient_id.eq.${catererUserId})`)
      .order('created_at', { ascending: true });
  },

  /** Check if user is admin (admin-caterer messages don't require active order) */
  isUserAdmin: async (userId) => {
    const { data, error } = await supabase.rpc('is_admin', { target_user: userId });
    if (error) {
      console.warn('is_admin RPC failed, falling back to false', error.message);
      return false;
    }
    return data === true;
  },

  getLatestActiveOrderWithCustomer: async (catererUserId, customerUserId) => {
    const { data: caterer, error: catererError } = await supabase
      .from('caterers')
      .select('id')
      .eq('user_id', catererUserId)
      .maybeSingle();

    if (catererError || !caterer?.id) {
      return { data: null, error: catererError || null };
    }

    return supabase
      .from('orders')
      .select('id, status, created_at')
      .eq('caterer_id', caterer.id)
      .eq('customer_id', customerUserId)
      .in('status', catererMessagingOperations.ACTIVE_CHAT_ORDER_STATUSES)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
  },

  sendMessage: async (catererUserId, customerUserId, messageData) => {
    const subject = messageData.subject || (messageData.order_id ? 'Order message' : 'Message');
    return supabase.from('messages').insert({
      sender_id: catererUserId,
      recipient_id: customerUserId,
      subject,
      body: messageData.body,
      related_order_id: messageData.order_id || null,
      related_event_id: messageData.event_id || null,
      created_at: new Date().toISOString(),
    });
  },

  markAsRead: async (messageId) => {
    return supabase
      .from('messages')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', messageId);
  },

  /** Mark all messages sent to this user (caterer) as read – e.g. when they view the inbox */
  markAllAsReadForUser: async (recipientUserId) => {
    return supabase
      .from('messages')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('recipient_id', recipientUserId)
      .eq('is_read', false);
  },

  getUnreadCount: async (catererUserId) => {
    const { count } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('recipient_id', catererUserId)
      .eq('is_read', false);
    return count ?? 0;
  },
};

/**
 * Realtime operations (for notifications)
 */
export const catererRealtimeOperations = {
  subscribeToMessages: (catererUserId, callback) => {
    return supabase
      .channel(`messages:${catererUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${catererUserId}`,
        },
        callback
      )
      .subscribe();
  },
  subscribeToOrdersForCaterer: (catererId, callback) => {
    return supabase
      .channel(`orders:caterer:${catererId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
          filter: `caterer_id=eq.${catererId}`,
        },
        callback
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `caterer_id=eq.${catererId}`,
        },
        callback
      )
      .subscribe();
  },
  subscribeToBidsForCaterer: (catererId, callback) => {
    return supabase
      .channel(`bids:caterer:${catererId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bids',
          filter: `caterer_id=eq.${catererId}`,
        },
        callback
      )
      .subscribe();
  },
  /**
   * Subscribe to platform notifications (admin broadcasts). Filter in callback for caterers/all.
   */
  subscribeToPlatformNotifications: (callback) => {
    return supabase
      .channel('platform-notifications-caterer')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'platform_notifications',
        },
        (payload) => {
          const row = payload?.new;
          if (!row) return;
          const rt = row?.recipient_type;
          if (rt === 'all' || rt === 'caterers') {
            callback(row);
          }
        }
      )
      .subscribe();
  },

  /** New special requests (events) – notify caterers so they can view incoming bids */
  subscribeToNewEvents: (callback) => {
    return supabase
      .channel('events:new:special-requests')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'events',
        },
        callback
      )
      .subscribe();
  },
  unsubscribe: (channel) => {
    return supabase.removeChannel(channel);
  },
};

/**
 * Sponsored placements operations
 */
export const sponsoredPlacementOperations = {
  getByCatererId: (catererId) =>
    supabase
      .from('sponsored_placements')
      .select('*')
      .eq('caterer_id', catererId)
      .order('start_date', { ascending: false }),

  create: (data) =>
    supabase.from('sponsored_placements').insert({
      ...data,
      is_active: false,
      payment_status: 'pending',
      updated_at: new Date().toISOString(),
    }).select().single(),

  activateAfterPayment: (placementId, catererId, paymentReference) =>
    supabase
      .from('sponsored_placements')
      .update({
        is_active: true,
        payment_status: 'completed',
        payment_reference: paymentReference,
        payment_date: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', placementId)
      .eq('caterer_id', catererId)
      .select()
      .single(),

  update: (id, catererId, data) =>
    supabase
      .from('sponsored_placements')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('caterer_id', catererId)
      .select()
      .single(),

  deactivate: (id, catererId) =>
    supabase
      .from('sponsored_placements')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('caterer_id', catererId),
};

/**
 * Tools Hub operations
 */
export const toolsOperations = {
  // Recipe costing records
  getRecipeCostings: (catererId) =>
    supabase
      .from('recipe_costings')
      .select('*')
      .eq('caterer_id', catererId)
      .order('updated_at', { ascending: false }),

  createRecipeCosting: (payload) =>
    supabase
      .from('recipe_costings')
      .insert(payload)
      .select('*')
      .single(),

  updateRecipeCosting: (id, catererId, payload) =>
    supabase
      .from('recipe_costings')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('caterer_id', catererId)
      .select('*')
      .single(),

  // CRM notes
  getClientNotes: (catererId, customerId) =>
    supabase
      .from('crm_client_notes')
      .select('*')
      .eq('caterer_id', catererId)
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false }),

  createClientNote: (payload) =>
    supabase
      .from('crm_client_notes')
      .insert(payload)
      .select('*')
      .single(),
};

/**
 * Email operations via Supabase Edge Function
 */
export const emailOperations = {
  sendEmail: async (payload) => supabase.functions.invoke('send-email', { body: payload }),

  sendVerificationReceipt: async ({ to, tier, amount, transactionRef }) => {
    return supabase.functions.invoke('send-email', {
      body: {
        to,
        subject: 'Your verification receipt',
        template: 'verification-receipt',
        data: { tier, amount, transactionRef },
      },
    });
  },

  sendOrderStatusEmail: async ({ to, order, statusLabel }) => {
    if (!to) return { error: new Error('Missing recipient email') };
    return supabase.functions.invoke('send-email', {
      body: {
        to,
        subject: `Your order is now ${statusLabel}`,
        template: 'order-status',
        data: {
          status: statusLabel,
          orderNumber: order?.id,
          eventName: order?.events?.title || order?.events?.name,
          deliveryTime: order?.estimated_delivery_time || order?.delivered_at,
          total: order?.total_amount,
        },
      },
    });
  },
};

export default supabase;
